import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Select, MenuItem, FormControl, InputLabel, Pagination, Stack, TableContainer,
  Card, CardContent, Divider, Chip
} from '@mui/material';
import { Add, Edit, Delete, PeopleAlt as PeopleIcon, Phone as PhoneIcon, Email as EmailIcon } from '@mui/icons-material';
import axios from 'axios';

const emptyCustomer = { name: '', phone: '', email: '', address: '' };

export default function Customers({ user, newCustomer }) {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCustomer);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    if (!user) return;
    axios.get('/api/customers', { headers: { Authorization: `Bearer ${user.token}` } })
      .then(res => setCustomers(res.data))
      .catch(() => setCustomers([]));
  }, [user]);

  // Add new customer from cashier page if provided
  useEffect(() => {
    if (newCustomer && !customers.find(c => c._id === newCustomer._id)) {
      setCustomers(cs => [newCustomer, ...cs]);
    }
    // eslint-disable-next-line
  }, [newCustomer]);

  const handleOpen = (customer) => {
    setEditing(customer || null);
    setForm(customer ? { ...customer } : emptyCustomer);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    try {
      if (editing) {
        const res = await axios.put(`/api/customers/${editing._id}`, form, { headers: { Authorization: `Bearer ${user.token}` } });
        setCustomers(cs => cs.map(c => c._id === editing._id ? res.data : c));
      } else {
        const res = await axios.post('/api/customers', form, { headers: { Authorization: `Bearer ${user.token}` } });
        setCustomers(cs => [res.data, ...cs]);
      }
      setOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving customer');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await axios.delete(`/api/customers/${id}`, { headers: { Authorization: `Bearer ${user.token}` } });
      setCustomers(cs => cs.filter(c => c._id !== id));
    } catch (err) {
      alert('Error deleting customer');
    }
  };

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.phone && c.phone.includes(search)) ||
    (c.email && c.email.toLowerCase().includes(search.toLowerCase()))
  );

  // Pagination logic
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const pagedCustomers = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Reset to page 1 if search or pageSize changes
  useEffect(() => { setPage(1); }, [search, pageSize]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f0f2f5' }}>
      {/* Left Panel - Customers List */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '2px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
          <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              Customers
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filtered.length} {filtered.length === 1 ? 'customer' : 'customers'} registered
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen(null)}>
            Add Customer
          </Button>
        </Box>

        {/* Search Bar */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Search Customers"
              placeholder="Search by name, phone, or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="page-size-label">Show</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                label="Show"
                onChange={e => setPageSize(Number(e.target.value))}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'white' }}>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedCustomers.map(c => (
                  <TableRow key={c._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                    </TableCell>
                    <TableCell>{c.phone || '—'}</TableCell>
                    <TableCell>{c.email || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.address || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => handleOpen(c)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(c._id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {pagedCustomers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        No customers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        {filtered.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>

      {/* Right Panel - Summary */}
      <Paper 
        elevation={3}
        sx={{ 
          width: { xs: '100%', md: 400 }, 
          display: 'flex', 
          flexDirection: 'column',
          maxHeight: '100%',
          overflow: 'auto'
        }}
      >
        {/* Summary Header */}
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            SUMMARY
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Statistics Cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* Total Customers */}
            <Card sx={{ boxShadow: 2, border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'primary.lighter',
                    color: 'primary.main'
                  }}>
                    <PeopleIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    Total Customers
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary.main">
                  {customers.length}
                </Typography>
              </CardContent>
            </Card>

            {/* Customers with Phone */}
            <Card sx={{ boxShadow: 1 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'success.lighter',
                    color: 'success.main'
                  }}>
                    <PhoneIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    With Phone
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="text.primary">
                  {customers.filter(c => c.phone).length}
                </Typography>
              </CardContent>
            </Card>

            {/* Customers with Email */}
            <Card sx={{ boxShadow: 1 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'info.lighter',
                    color: 'info.main'
                  }}>
                    <EmailIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    With Email
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="text.primary">
                  {customers.filter(c => c.email).length}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Info Box */}
          <Card sx={{ bgcolor: 'info.lighter', boxShadow: 0 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="info.dark" fontWeight={500} gutterBottom>
                💡 Quick Tips
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                <Typography component="li" variant="caption" sx={{ mb: 0.5 }}>
                  Click "Add Customer" to register new customers
                </Typography>
                <Typography component="li" variant="caption" sx={{ mb: 0.5 }}>
                  Use search to quickly find customers
                </Typography>
                <Typography component="li" variant="caption">
                  Edit or delete customers using action buttons
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Paper>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editing ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Name" name="name" value={form.name} onChange={handleChange} fullWidth required />
          <TextField margin="dense" label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Email" name="email" value={form.email} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Address" name="address" value={form.address} onChange={handleChange} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
